import React, { Component, useState } from "react"
import { Obstacle, RootState } from ".."
import produce from 'immer'
import Draggable from 'react-draggable';
import { SVGEditorGlobals, View } from "./SVGEditor";
import { useSelector } from "react-redux";
import convert from 'color-convert'
import { ColorScheme, lighten, shades } from "../Utils/Color";
import { useGesture } from "@use-gesture/react";
import { getTimeMeasureUtils } from "@reduxjs/toolkit/dist/utils";

export { ObstacleSVGViewer }

function ObstacleSVGViewer(props: {
  obstacle: Obstacle
  onChange: (obstacle: Obstacle) => void
  globals: SVGEditorGlobals
  fillColor?: string
  strokeColor?: string
  strokeWidth?: number
  editable?: boolean
}) {
  const cancelGesture = useGesture({
    onDrag: state => { state.event.stopPropagation() }
  }, {})

  const addPoint = (index: number) => {
    return () => {
      const point: [number, number] = [0, 0]
      props.onChange(produce(props.obstacle, (draft: Obstacle) => {
        draft.points.splice(index, 0, point)
      }))
    }
  }

  const deletePoint = (index: number) => {
    return () => {
      props.onChange(produce(props.obstacle, (draft: Obstacle) => {
        draft.points.splice(index, 1)
      }))
    }
  }

  const changePoint = (index: number) => {
    return (point: [number, number]) => {
      props.onChange(produce(props.obstacle, (draft: Obstacle) => {
        draft.points.splice(index, 1, point)
      }))
    }
  }

  const changeObstacle = (obstacle: Obstacle) => {
    props.onChange(produce(obstacle, (draft: Obstacle) => { }))
  }

  const centroid = (points: [number, number][]): [number, number] => {
    const sum = points.reduce((acc, current) => {
      return [acc[0] + current[0], acc[1] + current[1]]
    }, [0, 0] as [number, number])
    return [sum[0] / points.length, sum[1] / points.length]
  }

  const [preview, setPreview] = useState({
    isActive: false,
    obstacle: props.obstacle
  } as {
    isActive: boolean,
    obstacle: Obstacle
  })

  const  [hover, setHover] = useState(false)
  const settings = useSelector((state: RootState) => state.settings)
  const colorScheme: ColorScheme = {
    color: settings.obstacleDefaultColor,
    highlightColor: lighten(settings.obstacleDefaultColor, 20),
    border: settings.obstacleDefaultColor,
    highlightBorder: '#ddd',
    shadow: '#222'
  }

  return <g
        onClick={(e) => {
          props.globals.selection.selected === props.obstacle.id ? props.globals.selection.setSelected(''): props.globals.selection.setSelected(props.obstacle.id)
          e.stopPropagation()
        }}
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
      >
      <ObstacleSVGShape
        obstacle={props.obstacle}
        colorScheme={colorScheme}
        highlighted={hover || props.globals.selection.selected === props.obstacle.id}
        preview={false}
      ></ObstacleSVGShape>
      { preview.isActive &&
        <ObstacleSVGShape
          obstacle={preview.obstacle}
          colorScheme={colorScheme}
          highlighted={hover || props.globals.selection.selected === props.obstacle.id}
          preview={true}
        ></ObstacleSVGShape>
      }
      { props.globals.selection.selected === props.obstacle.id &&
      <Draggable
        scale={props.globals.view.scale}
        position={(() => { const c = centroid(props.obstacle.points); return {x: c[0], y: c[1]}})()}
        onDrag={(e, d) => {
          e.stopPropagation()
          setPreview({
            isActive: true,
            obstacle: produce(props.obstacle, draft => {
              const center = centroid(props.obstacle.points)
              draft.points = props.obstacle.points.map(point => [point[0] + d.x - center[0], point[1] + d.y - center[1]])
            })
          })
        }}
        onStop={(e, d) => {
          e.stopPropagation()
          changeObstacle(produce(props.obstacle, draft => {
            const center = centroid(props.obstacle.points)
            draft.points = props.obstacle.points.map(point => [point[0] + d.x - center[0], point[1] + d.y - center[1]])
          }))
          setPreview({
            isActive: false,
            obstacle: props.obstacle
          })
        }}
      >
        <circle
          {...cancelGesture()}
          className='position-interactionpoint'
          cx={0}
          cy={0}
          r={settings.positionInteractionPointRadius / props.globals.view.scale}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
        </circle>
      </Draggable>
      }
      {props.globals.selection.selected === props.obstacle.id && props.obstacle.points.map((point, index) => {
        const x = Number.isNaN(point[0]) ? 0 : point[0]
        const y = Number.isNaN(point[1]) ? 0 : point[1]
        return <g
            onContextMenu={(e) => {
              deletePoint(index)()
              e.stopPropagation()
            }}
          >
          <Draggable
            scale={props.globals.view.scale}
            position={{x,y}}
            onDrag={(_, d) => {
              setPreview({
                isActive: true,
                obstacle: produce(props.obstacle, draft => {
                  draft.points[index] = [d.x, d.y]
                })
              })
            }}
            onStop={(_, d) => {
              changePoint(index)([d.x, d.y])
              setPreview({
                isActive: false,
                obstacle: props.obstacle
              })
            }}
          >
            <circle
              key={index}
              className='interactionpoint'
              {...cancelGesture()}
              cx={0}
              cy={0}
              r={settings.interactionPointRadius / props.globals.view.scale}
              onClick={(e) => e.stopPropagation()}
            >
            </circle>
          </Draggable>
          </g>
      })}
    </g>
}

function ObstacleSVGShape(props: {
  obstacle: Obstacle
  colorScheme: ColorScheme
  highlighted: boolean
  preview: boolean
}) {
  return <g
      style={{
        opacity: props.preview ? 0.5 : 1
      }}
    >
    <polygon
      points={props.obstacle.points.map(p => `${p[0]},${p[1]}`).join(' ')}
      fill={props.highlighted ? props.colorScheme.highlightColor: props.colorScheme.color}
      style={{
        fill: props.highlighted ? props.colorScheme.highlightColor : props.colorScheme.color,
        opacity: props.highlighted ? 1 : 1,
        strokeWidth: props.highlighted ? 2 : 0,
        stroke: props.highlighted ? props.colorScheme.highlightBorder : props.colorScheme.border,
        strokeLinejoin: 'round',
        transition: 'all 0.15s ease-in'
      }}
    >
    </polygon>
    </g>
}
